import { CompactText } from 'ui/CompactText';
import type React from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';

import {
  DEFAULT_BULLETIN_FILTERS,
  useBulletinSearchFilters,
  countActiveBulletinFilters,
} from 'hooks/useBulletinSearchFilters';
import { useBulletinPublic } from 'hooks/useBulletin';
import { useDebounce } from 'hooks/useDebounce';
import { Ban, Circle, CircleX, Clock3, Info, Lock, Pencil, Send, Sparkles } from 'lucide-react';
import { CabinetButton, EmptyState } from 'ui/kit/Kit';
import { GoalIllustration } from 'ui/GoalIllustration/GoalIllustration';
import { StatusMark, type StatusTone } from 'ui/kit/StatusMark';
import { FadeIn, staggerDelay } from 'ui/kit/uiMotion';
import {
  formatBulletinGrades,
  formatBulletinSchedule,
  formatBulletinSubjectTitle,
  translateStudentType,
  translateStudyLocations,
  translateTutorResponseLabel,
} from 'utils/bulletinLabels';
import { resolveTutorChatUrl } from 'utils/openTutorChat';

import { useGetBulletins } from '../model/services';
import type { TeacherSearchProps } from '../types';
import { type FilterFormValues, TeacherSearchFilters } from './TeacherSearchFilters';
import styles from './TeacherSearch.module.scss';

import { Formik, useFormikContext } from 'formik';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

function hourPrice(from?: number | null, to?: number | null): string {
  const fmt = (value: number) => new Intl.NumberFormat('ru-RU').format(value);
  if (from && to) return `${fmt(from)}-${fmt(to)} руб. / час`;
  if (to) return `До ${fmt(to)} руб. / час`;
  if (from) return `От ${fmt(from)} руб. / час`;
  return 'Цена не указана';
}

function responseMark(label: string): { label: string; word: string; tone: StatusTone; icon: typeof Send } {
  if (label === 'Отклик отправлен') return { label, word: 'Отправлен', tone: 'blue', icon: Send };
  if (label === 'Отклик отклонен') return { label, word: 'Отклонен', tone: 'rose', icon: CircleX };
  if (label === 'Отклик не отправлен') return { label, word: 'Нет', tone: 'slate', icon: Circle };
  return { label, word: 'Отклик', tone: 'blue', icon: Info };
}

function bulletinStateMark(
  status?: string | null,
  closed?: boolean,
): { label: string; word: string; tone: StatusTone; icon: typeof Lock } | null {
  if (closed) return { label: 'Заявка закрыта', word: 'Закрыта', tone: 'slate', icon: Lock };
  if (status === 'pending_review') return { label: 'На модерации', word: 'Модерация', tone: 'amber', icon: Clock3 };
  if (status === 'rejected') return { label: 'Отклонена модератором', word: 'Отклонена', tone: 'rose', icon: CircleX };
  if (status === 'changes_requested') return { label: 'Нужны правки модератора', word: 'Правки', tone: 'orange', icon: Pencil };
  return null;
}

const TeacherSearchContent: React.FC = () => {
  const { values, resetForm } = useFormikContext<FilterFormValues>();
  const debouncedFilters = useDebounce(values, 400);
  const { save, clear } = useBulletinSearchFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [clearedNewIds, setClearedNewIds] = useState<Set<number>>(() => new Set());

  const markNotNew = (bulletinId: number) => {
    setClearedNewIds((prev) => {
      if (prev.has(bulletinId)) return prev;
      const next = new Set(prev);
      next.add(bulletinId);
      return next;
    });
    queryClient.setQueriesData({ queryKey: ['bulletins'] }, (old: any) => {
      if (!old?.items || !Array.isArray(old.items)) return old;
      return {
        ...old,
        items: old.items.map((it: any) =>
          it?.id === bulletinId ? { ...it, is_new: false } : it,
        ),
      };
    });
  };

  const limit = 5;
  const pageFromUrl = Math.max(0, Number(searchParams.get('page') || '1') - 1);
  const offset = pageFromUrl * limit;

  const setPage = (pageIndex: number) => {
    const next = new URLSearchParams(searchParams);
    if (pageIndex <= 0) {
      next.delete('page');
    } else {
      next.set('page', String(pageIndex + 1));
    }
    setSearchParams(next, { replace: true });
  };

  const prevFiltersRef = useRef<string | null>(null);

  useEffect(() => {
    save(debouncedFilters);
    const serialized = JSON.stringify(debouncedFilters);
    if (prevFiltersRef.current !== null && prevFiltersRef.current !== serialized) {
      setPage(0);
    }
    prevFiltersRef.current = serialized;
  }, [debouncedFilters, save]);

  const apiPriceFrom = debouncedFilters?.priceFrom
    ? Number(debouncedFilters.priceFrom)
    : null;
  const apiPriceTo = debouncedFilters?.priceTo ? Number(debouncedFilters.priceTo) : null;

  const apiGrades =
    debouncedFilters?.grade && debouncedFilters.grade.length > 0
      ? debouncedFilters.grade.map(Number)
      : undefined;

  const apiSubjectSubcategories =
    debouncedFilters?.subjectSubcategories?.length > 0
      ? debouncedFilters.subjectSubcategories
      : undefined;

  const apiStudentTypes =
    debouncedFilters?.learnerAudience && debouncedFilters.learnerAudience.length > 0
      ? debouncedFilters.learnerAudience
      : undefined;

  const apiStudyLocations =
    debouncedFilters?.studyLocations?.length > 0
      ? debouncedFilters.studyLocations
      : undefined;

  const { data, isLoading, isError, refetch } = useGetBulletins({
    ...(apiPriceFrom != null ? { min_price: apiPriceFrom } : {}),
    ...(apiPriceTo != null ? { max_price: apiPriceTo } : {}),
    subject_subcategories: apiSubjectSubcategories,
    student_types: apiStudentTypes,
    grades: apiGrades,
    study_locations: apiStudyLocations,
    mode: 'any',
    limit: limit,
    offset: offset,
  });

  const focusId = Math.max(0, Number(searchParams.get('focus') || '0')) || 0;
  const { data: focusedBulletin } = useBulletinPublic(focusId);

  useEffect(() => {
    if (focusId > 0 && pageFromUrl > 0) {
      setPage(0);
    }
  }, [focusId]);

  useEffect(() => {
    if (focusId > 0) {
      void refetch();
    }
  }, [focusId, refetch]);

  const rawItems = data?.items || [];
  const hiddenByFilters = Boolean(
    focusedBulletin &&
      focusId > 0 &&
      !rawItems.some((item: { id?: number }) => item.id === focusId),
  );
  const bulletins = useMemo(() => {
    const items = [...rawItems];
    if (!focusedBulletin || focusId <= 0) {
      return items;
    }
    const withoutFocus = items.filter((item: { id?: number }) => item.id !== focusId);
    return [{ ...focusedBulletin, is_new: true }, ...withoutFocus];
  }, [rawItems, focusedBulletin, focusId]);
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const navigate = useNavigate();

  const goToChat = async (item: {
    id: number;
    id_user?: number | null;
    my_bulletin_response_id?: number | null;
    pair_chat_id?: number | null;
    pair_room_id?: string | null;
  }) => {
    try {
      const url = await resolveTutorChatUrl(item);
      navigate(url);
    } catch (error) {
      console.error('Ошибка открытия чата:', error);
      navigate('/teacher/chat');
    }
  };

  const resetFilters = () => {
    clear();
    resetForm({ values: { ...DEFAULT_BULLETIN_FILTERS } });
    save(DEFAULT_BULLETIN_FILTERS);
  };

  const filtersActive = countActiveBulletinFilters(values) > 0;

  return (
    <div className={styles.page}>
      <header className={styles.heading}>
        <PageHead crumb={<>Личный кабинет / Поиск заявок</>} title={<>Поиск заявок</>} />
      </header>

      {/* Как было до редизайна: список слева, фильтры в правой колонке (sticky). До 1100px - сверху,
          до 800px - сворачиваемые (кнопка-заголовок в TeacherSearchFilters). */}
      <div className={styles.layout}>
      <aside className={styles.sidebar} data-search-sidebar>
        <TeacherSearchFilters />
      </aside>
      <div className={styles.results}>
      {hiddenByFilters && focusedBulletin ? (
        <CompactText className={styles.hint} tone={'info'}>
          Новая заявка не совпала с текущими фильтрами - показана сверху.
          {filtersActive ? (
            <>
              {' '}
              <button type='button' className={styles.textBtn} onClick={resetFilters}>
                Сбросить фильтры
              </button>
            </>
          ) : null}
        </CompactText>
      ) : null}

      {isLoading && <BrandLoader center label='Загрузка...' />}
      {isError && (
        <EmptyState
          title='Список не открылся'
          fact='Заявки на месте. Можно обновить список.'
          action={<CabinetButton onClick={() => void refetch()}>Обновить список</CabinetButton>}
        />
      )}
      {!isLoading && !isError && bulletins.length === 0 && (
        <EmptyState
          title='Пока нет заявок'
          fact='Новые заявки учеников появятся здесь. Если ждете конкретную, сбросьте фильтры.'
          action={
            <CabinetButton onClick={filtersActive ? resetFilters : () => void refetch()}>
              {filtersActive ? 'Сбросить фильтры' : 'Обновить список'}
            </CabinetButton>
          }
        />
      )}

      {!isLoading && !isError && bulletins.length > 0 ? (
        <ul className={styles.list}>
          {bulletins.map((item, index) => {
            const hasResponse = !!(item.my_bulletin_response_id || item.pair_chat_id);
            const canRespond = item.tutor_can_respond !== false;
            const actionLabel = hasResponse ? 'Перейти к чату' : 'Откликнуться';
            const responseHint = hasResponse
              ? null
              : translateTutorResponseLabel(item.my_bulletin_response_label_ru);
            const showNew = Boolean(item.is_new) && !clearedNewIds.has(item.id);
            const showUnavailable = !hasResponse && !canRespond;
            const unavailableReason =
              (typeof item.tutor_respond_block_reason === 'string' &&
                item.tutor_respond_block_reason.trim()) ||
              'Отклик по этой заявке сейчас недоступен.';
            const grade = formatBulletinGrades(item);
            const audience = translateStudentType(item.student_type);
            const subtitle = [grade, audience].filter(Boolean).join(' / ');
            const wish = item.wishes?.trim() || '';
            const place = translateStudyLocations(item.study_locations);
            // «Когда удобно» рядом с форматом: «Онлайн · Сб, 10:00-13:00»
            const placeLine = [place, formatBulletinSchedule(item)].filter(Boolean).join(" · ");
            const price = hourPrice(item.price_from, item.price_to);

            return (
              <FadeIn key={item.id} delay={staggerDelay(index)}>
                <li>
                  <article
                    className={styles.card}
                    onClick={() => {
                      markNotNew(item.id);
                      navigate(`/teacher/search/${item.id}`);
                    }}
                  >
                    <GoalIllustration subject={item.subject_category} className={styles.cardArt} fit='cover' />
                    <div className={styles.cardCopy}>
                      {(() => {
                        const marks = [];
                        if (showNew) {
                          marks.push(
                            <StatusMark key="new" label="Новая заявка" word="Новая" tone="blue" icon={Sparkles} />,
                          );
                        }
                        const state = bulletinStateMark(item.verification_status, item.closed);
                        if (state) {
                          marks.push(
                            <StatusMark key="state" label={state.label} word={state.word} tone={state.tone} icon={state.icon} />,
                          );
                        }
                        if (showUnavailable) {
                          marks.push(
                            <StatusMark key="blocked" label={unavailableReason} word="Недоступна" tone="amber" icon={Ban} />,
                          );
                        } else if (responseHint && responseHint !== 'Отклик не отправлен') {
                          // r5: метка «Нет» (отклик не отправлен) без контекста непонятна - об этом и так говорит кнопка «Откликнуться».
                          const mark = responseMark(responseHint);
                          marks.push(
                            <StatusMark key="response" label={mark.label} word={mark.word} tone={mark.tone} icon={mark.icon} />,
                          );
                        }
                        return marks.length ? <div className={styles.statusRow}>{marks}</div> : null;
                      })()}
                      <h2 className={styles.cardTitle}>
                        {formatBulletinSubjectTitle(item.subject_category)}
                      </h2>
                      {subtitle ? <p className={styles.muted}>{subtitle}</p> : null}
                      {wish ? (
                        <p className={styles.cardBody} title={wish}>
                          {wish}
                        </p>
                      ) : null}
                      {placeLine ? <p className={`${styles.muted} ${styles.metaLine}`}>{placeLine}</p> : null}
                    </div>
                    <div className={styles.cardSide}>
                      <p className={styles.price}>{price}</p>
                      <button
                        type='button'
                        className={styles.primarySm}
                        disabled={!hasResponse && !canRespond}
                        onClick={(event) => {
                          event.stopPropagation();
                          markNotNew(item.id);
                          if (hasResponse) {
                            void goToChat(item);
                            return;
                          }
                          navigate(`/teacher/search/${item.id}?step=respond`);
                        }}
                      >
                        {actionLabel}
                      </button>
                    </div>
                  </article>
                </li>
              </FadeIn>
            );
          })}
        </ul>
      ) : null}

      {totalPages > 1 ? (
        <nav className={styles.pager} aria-label='Страницы'>
          {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
            const pageNum = idx + 1;
            const isSelected = currentPage === pageNum;
            return (
              <button
                key={pageNum}
                type='button'
                className={isSelected ? styles.pagerCurrent : undefined}
                onClick={() => setPage(idx)}
              >
                {pageNum}
              </button>
            );
          })}
          {totalPages > 5 ? <span className={styles.muted}>...</span> : null}
          <button
            type='button'
            disabled={currentPage === totalPages}
            onClick={() => setPage(currentPage)}
          >
            Дальше
          </button>
        </nav>
      ) : null}
      </div>
      </div>
    </div>
  );
};

export const TeacherSearch: React.FC<TeacherSearchProps> = () => {
  const { load } = useBulletinSearchFilters();

  const initialValues = useMemo(() => load(), [load]);

  return (
    <Formik initialValues={initialValues} enableReinitialize={false} onSubmit={() => {}}>
      <TeacherSearchContent />
    </Formik>
  );
};
